import React, { useEffect, useState } from 'react';
import { getSellerProfile, updateSellerProfile } from '../../../../lib/utils/apiCalls';
import { getCachedMediaUrl, getInitials, getMediaUrl, loadImage } from '../../../../lib/utils/utils';
import EditIcon from '../../../../Assets/OptionsModel/edit.svg';
import LockIcon from '../../../../Assets/Account/PasswordIcon.svg';
import UserIcon from '../../../../Assets/Account/NameIcon.svg';
import EmailIcon from '../../../../Assets/Account/EmailIcon.svg';
import PhoneIcon from '../../../../Assets/Account/PhoneIcon.svg';
import ImageAdd from '../../../../Assets/ImageAdd.svg';
import imageCompression from 'browser-image-compression';
import { showToast } from '../../../../Components/Toaster/Toaster';
import ChevronDown from '../../../../Assets/ChevronDownLargeDarkMode.svg';
import { useDispatch } from 'react-redux';
import { setSettingsModal } from '../../../../Features/settingsSlice';

function MyProfile() {
    const dispatch = useDispatch();

    const [sellerProfile, setSellerProfile] = useState({});
    const [editFormOpen, setEditFormOpen] = useState(false);

    const defaultFormInputs = {
        name: '',
        phoneNumber: '',
    };

    const [formInputs, setFormInputs] = useState(defaultFormInputs);
    const [formErrors, setFormErrors] = useState(defaultFormInputs);
    const [profileImage, setProfileImage] = useState({
        file: null,
        src: null,
        url: null,
    });

    function handleChange(e) {
        const { name, value } = e.target;

        setFormInputs({ ...formInputs, [name]: value });
    }

    function handleImageChange(e) {
        const file = e.target.files[0];
        processFile(file);
        e.target.value = null;
    }

    function handleRemoveImage() {
        setProfileImage({
            url: null,
            file: null,
            src: null,
        });
    }

    async function processFile(file) {
        if(file) {
            const img = await loadImage(file);
            const selectedImage = {
                ...profileImage,
                src: img.src,
                file: file
            };
            setProfileImage(selectedImage);
        }
        else {
            return;
        }
    }

    function handleEditProfile() {
        setEditFormOpen(true);
    }

    function handleCancelForm() {
        setEditFormOpen(false);
    }

    function handleEditPassword() {
        dispatch(setSettingsModal('change-password'));
    }

    async function handleSubmit() {
        const validationErrors = {};

        if(!formInputs.name) {
            validationErrors.name = 'Required field';
        }

        if(!formInputs.phoneNumber) {
            validationErrors.phoneNumber = 'Required field';
        }

        if(Object.keys(validationErrors).length === 0) {
            setFormErrors(defaultFormInputs);

            const callBody = {
                name: formInputs.name,
                phoneNumber: formInputs.phoneNumber,
            }

            if(profileImage.src) {
                let url = '';

                if(profileImage.file.size > 1048576) {
                    try {
                        let compressedBlob = await imageCompression(profileImage.file, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true
                        });
    
                        let compressedFile = new File([compressedBlob], profileImage.file.name, { type: profileImage.file.type, lastModified: Date.now() });
                    
                        if (compressedFile) {
                            url = await getMediaUrl(compressedFile);
                        }
                        else {
                            url = await getMediaUrl(profileImage.file);
                        }
                    }
                    catch(err) {
                        url = await getMediaUrl(profileImage.file);
                    }
                }
                else {
                    url = await getMediaUrl(profileImage.file);
                }

                callBody.profilePic = url;
            }
            else {
                callBody.profilePic = sellerProfile.profilePic;
            }

            try {
                const response = await updateSellerProfile(callBody);
                if(response.status === 200) {
                    fetchSellerProfile();
                    showToast('Profile updated.', 'success');
                    setEditFormOpen(false);
                }
            }
            catch(err) {
                showToast('Failed to update profile.', 'error');
            }
        }
        else {
            setFormErrors(validationErrors);
        }
    }

    async function fetchSellerProfile() {
        try {
            const response = await getSellerProfile();
            if(response.status === 200) {
                setSellerProfile(response.data.data);
                setFormInputs({
                    name: response.data.data.name,
                    phoneNumber: response.data.data.phoneNumber,
                });
                setProfileImage({
                    ...profileImage,
                    url: response.data.data.profilePic,
                });
            }
        }
        catch(err) {
            console.log(err);
        }
    }

    useEffect(() => {
        fetchSellerProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [hoverOnDiv, setHoverOnDiv] = useState(false);
    const [buttonVisible, setButtonVisible] = useState(false);

    useEffect(() => {
        if(hoverOnDiv) {
            setButtonVisible(true);
        }
        else {
            setButtonVisible(false);
        }
    }, [hoverOnDiv]);

    return (
        <div className='w-3/5 flex flex-col gap-4'>
            <div className="p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h1 className='font-semibold text-[20px] leading-6 text-[#121212]'>My Profile</h1>

                    {
                        !editFormOpen && 
                        <button onClick={handleEditProfile} className="outline-none size-6 flex items-center justify-center">
                            <img className='size-full' src={EditIcon} alt="" />
                        </button>
                    }
                </div>

                {
                    !editFormOpen && 
                    <div className="flex flex-col gap-4 font-medium leading-5">
                        <div className="p-[10px] border-b border-[#EAEAEA]">
                            <div className="size-20 rounded-full overflow-hidden flex items-center justify-center bg-[#EAEAEA]">
                                {
                                    sellerProfile.profilePic ? 
                                    <img className='size-full' src={getCachedMediaUrl(sellerProfile.profilePic)} alt={sellerProfile.name} /> : 
                                    <h1 className='font-semibold text-3xl text-[#666666]'>{getInitials(sellerProfile.name)}</h1>
                                }
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-[10px] border-b border-[#EAEAEA]">
                            <div className="size-6 flex items-center justify-center">
                                <img src={UserIcon} alt="" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <h1 className='text-[14px] text-[#808080]'>Name</h1>

                                <p className='text-[16px] text-[#000000]'>{sellerProfile.name}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-[10px] border-b border-[#EAEAEA]">
                            <div className="size-6 flex items-center justify-center">
                                <img src={EmailIcon} alt="" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <h1 className='text-[14px] text-[#808080]'>Email Id</h1>

                                <p className='text-[16px] text-[#000000]'>{sellerProfile.email}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-[10px]">
                            <div className="size-6 flex items-center justify-center">
                                <img src={PhoneIcon} alt="" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <h1 className='text-[14px] text-[#808080]'>Phone Number</h1>

                                <p className='text-[16px] text-[#000000]'>{sellerProfile.phoneNumber}</p>
                            </div>
                        </div>
                    </div>
                }
            </div>

            {
                !editFormOpen && 
                <div className="p-4 flex items-center justify-between rounded-xl shadow-[0_0_6px_0_#0000001F]">
                    <div className="flex items-center gap-3">
                        <div className="size-6 flex items-center justify-center">
                            <img src={LockIcon} alt="" />
                        </div>

                        <div className="flex flex-col text-[#000000] font-medium">
                            <h1 className='text-[16px] leading-5'>Change Password</h1>

                            <p className='text-[14px] leading-5 text-[#808080]'>&#40;Enter your current password to make new password&#41;</p>
                        </div>
                    </div>

                    <button onClick={handleEditPassword} className="outline-none size-6 flex items-center justify-center">
                        <img className='size-full' src={EditIcon} alt="" />
                    </button>
                </div>
            }

            {
                editFormOpen && 
                <div className="flex flex-col gap-8">
                    <div className='flex flex-col gap-4'>
                        <div className="p-4 border border-[#EAEAEA] flex items-center gap-4 rounded-xl">
                            <div className="flex flex-col gap-1 font-medium">
                                <h1 className='text-[18px] leading-[22px] text-[#000000]'>Upload your profile picture</h1>

                                <p className='text-[14px] leading-[17px] text-[#808080]'>&#40;This picture displayed in your profile&#41;</p>
                            </div>

                            <div className="flex items-center gap-3 font-semibold text-[14px] leading-[17px]">
                                <div 
                                    onMouseEnter={() => setHoverOnDiv(true)} 
                                    onMouseLeave={() => setHoverOnDiv(false)} 
                                    className="size-20 rounded-full overflow-hidden bg-[#EAEAEA] flex items-center justify-center relative"
                                >
                                    {
                                        profileImage.file ? 
                                        <img className='size-full object-cover' src={profileImage.src} alt="" /> : 
                                        (
                                            profileImage.url ? 
                                            <img className='size-full object-cover' src={profileImage.url} alt="" /> : 
                                            <label htmlFor='profile-image' className='cursor-pointer font-semibold text-2xl text-[#666666]'>{getInitials(sellerProfile.name)}</label>
                                        )
                                    }
                                    <label
                                        htmlFor="profile-image"
                                        className={`absolute size-full top-0 left-0 bg-[#00000095] flex items-center justify-center cursor-pointer transition-opacity duration-200 ${buttonVisible ? "opacity-100 visible" : "opacity-0 invisible"}`}
                                    >
                                        <img className="scale-75" src={ImageAdd} alt="" />
                                    </label>

                                    <input 
                                        className='hidden' 
                                        name='profile-image' 
                                        id='profile-image' 
                                        type="file" 
                                        accept='image/*' 
                                        onChange={handleImageChange} 
                                    />
                                </div>

                                <label htmlFor='profile-image' onChange={handleImageChange} className='cursor-pointer font-semibold text-[14px] leading-[17px] border border-[#EAEAEA] text-[#666666] px-4 py-2 rounded'>Replace</label>

                                <button onClick={handleRemoveImage} className='px-4 py-2 bg-[#EF4444] text-[#FFFFFF] rounded'>Remove</button>
                            </div>
                        </div>

                        <div className="border border-[#EAEAEA] p-4 rounded-xl flex flex-col gap-6">
                            <h1 className='font-medium text-[18px] leading-[22px] text-[#000000]'>Personal Information</h1>

                            <div className="flex items-center justify-between">
                                <label htmlFor="name" className='font-medium text-[16px] leading-5 text-[#000000]'>Full Name</label>

                                <div className="w-[70%] flex flex-col gap-2">
                                    <input 
                                        name='name' 
                                        id='name' 
                                        className='outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]'
                                        type="text" 
                                        placeholder='enter full name' 
                                        value={formInputs.name} 
                                        onChange={handleChange} 
                                    />

                                    {
                                        formErrors.name && 
                                        <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.name}</p>
                                    }
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <p className='font-medium text-[16px] leading-5 text-[#000000]'>Email Address</p>

                                <div className="w-[70%] border border-[#EAEAEA] bg-[#EAEAEA] px-3 py-4 rounded-lg font-normal text-base text-[#999999]">
                                    <p>{sellerProfile.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <label htmlFor="phoneNumber" className='font-medium text-[16px] leading-5 text-[#000000]'>Mobile Number</label>

                                <div className="w-[70%] flex flex-col gap-2">
                                    <div className="w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg flex items-center gap-2">
                                        <p className="size-6 font-normal text-base text-[#666666]">+91</p>

                                        <div className="size-6 flex items-center justify-center">
                                            <img src={ChevronDown} alt="" />
                                        </div>

                                        <input 
                                            name='phoneNumber' 
                                            id='phoneNumber' 
                                            className='w-[calc(100%-24px-24px)] bg-transparent outline-none font-normal text-base placeholder:text-[#666666] text-[#000000]'
                                            type="text" 
                                            placeholder='enter phone number' 
                                            value={formInputs.phoneNumber} 
                                            onChange={handleChange} 
                                        />
                                    </div>

                                    {
                                        formErrors.phoneNumber && 
                                        <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.phoneNumber}</p>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-3 font-semibold text-base text-[#121212]">
                        <button onClick={handleCancelForm} className='border border-[#808080] rounded px-4 py-2'>Cancel</button>

                        <button onClick={handleSubmit} className='bg-primaryColor hover:bg-primaryAccent rounded px-4 py-2 text-buttonText'>Save Changes</button>
                    </div>
                </div>
            }
        </div>
    );
}

export default MyProfile;
